/**
 * FRONT-2716: a tool card prints the tool's raw INPUT when expanded, and a
 * person who gives Eddy an API key for an MCP server sees that key in plain
 * text there (`local_mcp_add` with `env.MCP_HEADER_AUTHORIZATION`). The
 * backend already masks the same values in the tool's OUTPUT (`redactEnv`,
 * `src/mcp/tools.ts`); this is the input-side twin, applied by every surface
 * that shows a tool's arguments: the desktop tool card, the phone trace card
 * and the approval card's details block.
 *
 * Display only — the real arguments still reach the tool untouched.
 *
 * What is masked (value replaced, key kept, so the person can still see WHICH
 * values were set):
 *  - every value under `env` of an MCP-management tool (`local_mcp_*`), the
 *    field those tools document as the place for a server's credential, plus
 *    credential-named query parameters in its `url` and credential-named
 *    flags in its `args`;
 *  - any `MCP_HEADER_*` key, anywhere in the input, on any tool, and any
 *    `MCP_HEADER_<NAME>=<value>` written inside a string;
 *  - a list of key names that only ever hold a credential.
 *
 * Not the Sentry scrubber's substring regex (`sentry.ts`): that one may hide
 * too much because nobody reads telemetry, while here a person reads the card,
 * and `key` / `maxTokens` / `tokenBudget` arguments must stay readable.
 */

export const REDACTED = '••••••••';

const MCP_HEADER_KEY = /^MCP_HEADER_/i;
/** `MCP_HEADER_X=value` anywhere in a string; the value runs to the end of
 *  the line, or to the closing quote when quoted. */
const MCP_HEADER_PAIR = /(MCP_HEADER_[A-Za-z0-9_]*\s*=\s*)("[^"\n]*"?|'[^'\n]*'?|[^\n]*)/gi;

/** Compared after lower-casing and dropping `_`/`-`, so `api_key`, `apiKey`
 *  and `X-Api-Key` all match. Exact names, never a substring. */
const SECRET_KEY_NAMES = new Set([
  'authorization',
  'proxyauthorization',
  'apikey',
  'xapikey',
  'apitoken',
  'token',
  'accesstoken',
  'refreshtoken',
  'bearertoken',
  'authtoken',
  'xauthtoken',
  'idtoken',
  'sessiontoken',
  'secret',
  'clientsecret',
  'secretkey',
  'secretaccesskey',
  'privatekey',
  'password',
  'passwd',
  'cookie',
]);

function isSecretName(key: string): boolean {
  return MCP_HEADER_KEY.test(key) || SECRET_KEY_NAMES.has(key.toLowerCase().replace(/[-_]/g, ''));
}

function redactString(value: string): string {
  return value.replace(MCP_HEADER_PAIR, `$1${REDACTED}`);
}

/** A value under a credential-named key: a scalar is the credential; an
 *  object (e.g. a JSON schema describing an `apiKey` field) is walked, so it
 *  stays readable while any secret inside it is still masked. */
function redactUnderSecretKey(value: unknown, depth: number): unknown {
  if (value == null) return value;
  if (typeof value === 'object') return redactDeep(value, depth);
  return REDACTED;
}

/** Real tool arguments are a few levels deep; anything past this is masked
 *  whole rather than walked, so a pathological input cannot exhaust the stack
 *  and nothing below the bound is ever shown unmasked. */
const MAX_DEPTH = 64;

function redactDeep(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') return redactString(value);
  if (value && typeof value === 'object' && depth >= MAX_DEPTH) return REDACTED;
  if (Array.isArray(value)) return value.map((v) => redactDeep(v, depth + 1));
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = isSecretName(k) ? redactUnderSecretKey(v, depth + 1) : redactDeep(v, depth + 1);
    }
    return out;
  }
  return value;
}

function isMcpManagementTool(toolName: string | undefined): boolean {
  return !!toolName && toolName.startsWith('local_mcp_');
}

function maskEveryValue(value: unknown): unknown {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return Object.fromEntries(Object.keys(value).map((k) => [k, REDACTED]));
  }
  return REDACTED;
}

/** `https://x/mcp?api_key=abc` → the value of each credential-named param masked. */
function redactUrl(url: string): string {
  return url.replace(/([?&])([^=&#]+)=([^&#]*)/g, (whole, sep: string, name: string) =>
    isSecretName(decodeURIComponentSafe(name)) ? `${sep}${name}=${REDACTED}` : whole,
  );
}

function decodeURIComponentSafe(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/** `--api-key abc` / `--token=abc` → the flag's value masked. */
function redactArgs(args: unknown[]): unknown[] {
  const flagName = (a: unknown) => (typeof a === 'string' ? /^--?([A-Za-z0-9_-]+)$/.exec(a)?.[1] : undefined);
  return args.map((a, i) => {
    if (typeof a !== 'string') return a;
    const inline = /^(--?([A-Za-z0-9_-]+)=)/.exec(a);
    if (inline && isSecretName(inline[2])) return `${inline[1]}${REDACTED}`;
    const prev = i > 0 ? flagName(args[i - 1]) : undefined;
    if (prev && isSecretName(prev)) return REDACTED;
    return a;
  });
}

function redactMcpManagementInput(input: Record<string, unknown>): Record<string, unknown> {
  const out = { ...input };
  if (out.env != null) out.env = maskEveryValue(out.env);
  if (typeof out.url === 'string') out.url = redactUrl(out.url);
  if (Array.isArray(out.args)) out.args = redactArgs(out.args);
  return out;
}

/** Text that is not valid JSON — typically a truncated or malformed tool call
 *  the SDK kept as raw text. Masks `"<secret name>": "value"` pairs and, for
 *  an MCP-management tool, every value inside the `"env": { … }` object. */
function redactRawText(toolName: string | undefined, text: string): string {
  let out = text.replace(/("([^"\\]+)"\s*:\s*")((?:[^"\\]|\\.)*)("?)/g, (whole, head: string, key: string, _v, tail: string) =>
    isSecretName(key) ? `${head}${REDACTED}${tail}` : whole,
  );
  if (isMcpManagementTool(toolName)) {
    out = out.replace(/("env"\s*:\s*\{)([^}]*)/g, (_w, head: string, body: string) =>
      head + body.replace(/(:\s*")((?:[^"\\]|\\.)*)("?)/g, `$1${REDACTED}$3`),
    );
  }
  return redactString(out);
}

/**
 * The tool input as a surface may show it. Returns a new value; never mutates
 * the input. A string input (some parts arrive pre-serialised) is parsed when
 * it is JSON so the same rules apply, and pattern-masked otherwise.
 */
export function redactToolInput(toolName: string | undefined, input: unknown): unknown {
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return JSON.stringify(redactToolInput(toolName, JSON.parse(trimmed)), null, 2);
      } catch {
        // Not JSON after all — fall through to pattern masking.
      }
    }
    return redactRawText(toolName, input);
  }
  const redacted = redactDeep(input);
  if (isMcpManagementTool(toolName) && redacted && typeof redacted === 'object' && !Array.isArray(redacted)) {
    return redactMcpManagementInput(redacted as Record<string, unknown>);
  }
  return redacted;
}

/** The masked input as the text a card prints. */
export function toolInputText(toolName: string | undefined, input: unknown): string {
  const shown = redactToolInput(toolName, input);
  return typeof shown === 'string' ? shown : JSON.stringify(shown, null, 2);
}
